import { ExternalLink } from '@/atoms/ExternalLink'
import { LazyHydrate } from '@/atoms/LazyHydrate'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { paths } from '@/constants/paths'
import { Seo } from '@/layout/Seo'
import { useCanonicalUrl } from '@/layout/Seo/useCanonicalUrl/useCanonicalUrl'
import { LegalLayout } from '@/layouts/LegalLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'

const bulletedList = 'list-disc list-inside'

const DevicesPage: NextPageWithLayout = () => {
  const canonical = useCanonicalUrl(paths.legal.devices)
  const title = 'Supported Devices | Angel'

  return (
    <div className="bg-white py-20 text-justify text-black">
      <Seo
        canonical={canonical}
        title={title}
        description="View the full list of supported devices for watching Angel content, including Smart TVs, streaming players, computers, and mobile devices."
        openGraph={{
          title,
          url: canonical,
          site_name: 'Angel',
        }}
        path={paths.legal.devices}
      />
      <LazyHydrate whenVisible>
        <PaddedContainer>
          <div className="flex flex-col gap-4 p-10">
            <h1 className="text-center text-[1.3rem] font-bold md:text-[2rem]">
              <p>Angel Studios</p>
              <p>Supported Devices</p>
            </h1>

            <p className="text-center">Updated On: September 25, 2024</p>
            <p>
              Watch Angel Studios’ award-winning titles from independent creators on one of our many supported devices.{' '}
            </p>

            <h2 className="py-2 text-center font-bold md:text-[1.5rem]">Smart TVs &amp; Streaming Media Players</h2>

            <p>
              Smart TVs running Samsung Tizen OS, Roku TV, Android TV, or Fire TV support Angel Studios without the need
              for an additional device. Angel Studios can also be watched on media players and streaming sticks that
              plug into any TV. Most TVs made within the last 5 years are compatible.
            </p>
            <p>The following are supported:</p>

            <p>
              <ul className={bulletedList}>
                <li>Apple TV</li>
                <li>Chromecast</li>
                <li>Google TV</li>
                <li>Roku</li>
                <li>Fire TV</li>
                <li>Microsoft Xbox</li>
                <li>LG TV</li>
                <li>Samsung TV</li>
                <li>VIZIO</li>
              </ul>
            </p>

            <h2 className="py-2 text-center font-bold md:text-[1.5rem]">Computers &amp; Laptops</h2>

            <p>
              Whether you own a Mac, Windows, Chromebook, or Linux computer - Angel Studios is optimized for today’s
              most popular web browsers.
            </p>
            <p>We recommend the following browsers:</p>

            <p>
              <ul className={bulletedList}>
                <li>Google Chrome</li>
                <li>Safari</li>
                <li>Mozilla Firefox</li>
                <li>Microsoft Edge</li>
              </ul>
            </p>

            <h2 className="py-2 text-center font-bold md:text-[1.5rem]">Smartphones &amp; Tablets</h2>

            <p>
              Angel Studios’ titles can be watched from anywhere on virtually any smartphone or tablet that runs Android
              8.0+, iOS 14.0+ or iPadOS 14.0+. Most mobile devices made within the last 5 years are compatible. The
              Angel Studios app can be downloaded from the{' '}
              <ExternalLink className="underline" href={paths.external.appleAppStore}>
                Apple App Store
              </ExternalLink>{' '}
              and the{' '}
              <ExternalLink className="underline" href={paths.external.googlePlayStore}>
                Google Play Store.
              </ExternalLink>
            </p>

            <p>The following are some examples of supported devices (this is not an exhaustive list):</p>

            <p>
              <ul className={bulletedList}>
                <li>Samsung Galaxy Phone (A6, S8, and newer)</li>
                <li>Samsung Galaxy Tablet (A7, S6, and newer)</li>
                <li>Google Pixel (3a and newer)</li>
                <li>Xiaomi Redmi (A5 and newer)</li>
                <li>Huawei (P20 and newer)</li>
                <li>OnePlus (6 and newer)</li>
                <li>Apple iPhone (8 and newer)</li>
                <li>Apple iPad Mini (5th generation and newer)</li>
                <li>Apple iPad (5th generation and newer)</li>
                <li>Apple iPad Air (4th generation and newer)</li>
                <li>Apple iPad Pro (1st generation and newer)</li>
              </ul>
            </p>
          </div>
        </PaddedContainer>
      </LazyHydrate>
    </div>
  )
}

DevicesPage.getLayout = (page) => {
  return <LegalLayout>{page}</LegalLayout>
}

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/legal/devices.tsx' },
  async ({ locale }) => {
    return {
      props: {
        ...(await loadTranslations(locale, ['common', 'account'])),
      },
      revalidate: false,
    }
  },
)

export const getStaticPaths = getGenericStaticPaths

export default DevicesPage
